import * as React from 'react';
import ITradeHistoryChart, { TTradeHistoryChart } from './ITradeHistoryChart';
import { IFilteredTradesList } from '../TradeHistory/ITradeHistory';
import Chart from 'react-apexcharts';
import moment from 'moment';
import Big from 'big.js';

const big = Big();
const purple = '#81109c';
const green = '#58b255';
const gridBorder = '#e9e9e9';

export default class TradeHistoryChart extends React.PureComponent<ITradeHistoryChart, TTradeHistoryChart> {

  constructor(props: ITradeHistoryChart) {
    super(props);
    const generatedDates = this.generateDates();

    this.state = {
      options: {
        chart: { id: 'chart', toolbar: { show: false }, zoom: { enabled: false } },
        colors: [green, purple],
        stroke: { width: [0, 3] },
        legend: { show: false },
        grid: { strokeDashArray: 5, borderColor: gridBorder },
        plotOptions: { bar: { columnWidth: generatedDates.length > 14 ? '70%' : '20%' } },
        responsive: [{
          breakpoint: 420,
          options: { chart: { height: '300px' }, xaxis: { labels: { formatter: this.dateFormatMobile } } },
        }],
        xaxis: {
          type: generatedDates.length > 7 ? 'datetime' : 'category',
          labels: {
            formatter: this.dateFormat,
          },
          categories: generatedDates,
        },
        yaxis: [{
          title: {
            text: 'Volume (USD)',
            style: { color: green },
          },
          labels: { style: { colors: green }, formatter: this.formatAmount },
        }, {
          opposite: true,
          title: {
            text: 'Share price (USD)',
            style: { color: purple },
          },
          labels: { style: { colors: purple }, formatter: this.formatAmount },
        }],
        tooltip: {
          x: {
            formatter: this.tooltipDateFormat,
          },
        },
      },
      series: [{
        name: 'Volume (USD)',
        type: 'column',
        data: this.getBarChartData(props.list, generatedDates),
      }, {
        name: 'Share price (USD)',
        type: 'line',
        data: this.getLineChartData(props.list, generatedDates),
      }],
    };
  }

  componentDidUpdate(prevProps: ITradeHistoryChart) {
    if (prevProps.list !== this.props.list) {
      const generatedDates = this.generateDates();

      this.setState({
        options: {
          ...this.state.options,
          xaxis: {
            ...this.state.options.xaxis,
            type: generatedDates.length > 7 ? 'datetime' : 'category',
            categories: generatedDates,
          },
          plotOptions: {
            bar: {
              columnWidth: generatedDates.length > 14 ? '70%' : '20%',
            },
          },
        },
        series: [{
          name: 'Volume (USD)',
          type: 'column',
          data: this.getBarChartData(this.props.list, generatedDates),
        }, {
          name: 'Share price (USD)',
          type: 'line',
          data: this.getLineChartData(this.props.list, generatedDates),
        }],
      });
    }
  }

  dateFormat = (value: string, timestamp: number) => {
    return moment(new Date(timestamp)).format('DD MMM \'YY');
  }

  dateFormatMobile = (value: string, timestamp: number) => {
    return moment(new Date(timestamp)).format('MMM \'YY');
  }

  tooltipDateFormat = (timestamp: number) => {
    return moment(new Date(timestamp)).format('DD MMM');
  }

  formatAmount = (value: number) => {
    const formatter: Intl.NumberFormat = new Intl.NumberFormat('sfb', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    });
  
    return formatter.format(value);
  }

  generateDates = () => {
    const firstDate = moment(this.props.startDate);
    const lastDate = moment(this.props.endDate);
    const daysDiff = lastDate.diff(firstDate, 'days');
    const dates = [];

    for (let i = 0; i <= daysDiff; i += 1) {
      dates.push(moment(firstDate).add(i, 'days').format('YYYY-MM-DD'));
    }

    return dates;
  }

  getBarChartData = (data: IFilteredTradesList[], generatedDates: string[]) => {
    type obj = { date: string; value: number };
    const barChartData: number[] = [];

    const reducedValue = data.reduce((prev: obj[], current: obj, index: number) => {
      const lastItem = prev.length - 1;
      if (index !== 0 && prev[lastItem].date === current.date) {
        prev[lastItem] = {
          date: current.date,
          value: Number(big(prev[lastItem].value).add(current.value)),
        };
        return prev;
      }

      prev.push({ date: current.date, value: current.value });
      return prev;
    }, []);


    generatedDates.forEach((date: string) => {
      const isInArray = reducedValue.find((el: obj) => el.date === date);
      if (isInArray) {
        barChartData.push(isInArray.value);
      } else {
        barChartData.push(0);
      }
    });

    return barChartData;
  }

  getLineChartData = (data: IFilteredTradesList[], generatedDates: string[]) => {
    type obj = { date: string; number_of_shares: number; value: number };
    const initialValue = this.getLineChartInitialValue();
    const lineChartData: number[] = [];

    const reducedData = data.reduce((prev: obj[], current: obj, index: number) => {
      const lastItem = prev.length - 1;
      if (index !== 0 && prev[lastItem].date === current.date) {
        prev[lastItem] = {
          date: current.date,
          number_of_shares: Number(big(prev[lastItem].number_of_shares).add(current.number_of_shares)),
          value:  Number(big(prev[lastItem].value).add(current.value)),
        };
        return prev;
      }

      prev.push({ date: current.date, number_of_shares: current.number_of_shares, value: current.value });
      return prev;
    }, []);

    generatedDates.forEach((date: string, i: number) => {
      const isInArray = reducedData.find((el: obj) => el.date === date);
      if (isInArray) {
        lineChartData.push(Number(big(isInArray.value).div(isInArray.number_of_shares)));
      } else {
        lineChartData.push(lineChartData[i - 1] || initialValue);
      }
    });

    return lineChartData;

  }

  getLineChartInitialValue = () => {
    if (this.props.lastFilledTrades) {
      const reduced = this.props.lastFilledTrades.reduce((prev: IFilteredTradesList, current: IFilteredTradesList) => {
        return {
          ...current,
          number_of_shares: Number(big(prev.number_of_shares).add(current.number_of_shares)),
          value:  Number(big(prev.value).add(current.value)),
        };
      });

      return Number(big(reduced.value).div(reduced.number_of_shares));
    }

    return 0;
  }

  render() {
    return (
      <Chart
        options={this.state.options}
        series={this.state.series}
        type="line"
        width="100%"
        height="400px"
      />
    );
  }
}
